import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {RateCard} from '@shared/models/rate-card.model';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {API} from '@configs/api.config';
import {Organization} from '../../organization/models/organization';
import {RateTier} from '@shared/models/rate-tier.model';
import {Pageable} from '@shared/models/pageable.model';
import {Vehicle} from '@shared/models/vehicle';
import {ContractType} from '@shared/models/enums/rate-sheet-contract-type.enum';

export interface RateSheetFilterRequest {
  types?: string[];
  organizationIds?: string[];
  statuses?: string[];
  operationIds?: string[];
  vehicleCategoryIds?: string[];
  validityPeriodFrom?: string;
  validityPeriodTo?: string;
  contractEffectiveDateFrom?: string;
  contractEffectiveDateTo?: string;
  searchKey?: string;
  contractType?: ContractType;
}
import {VehicleCategory} from '@shared/models/vehicle-category.model';
import {ResourceType} from '@shared/models/enums/resource.type';
import {Consignee} from '../../reporting/models/consignee.model';
import {OrganizationLocation} from '../../organization/models/organization-location';
import {MatrixDataTemplate} from 'app/modules/organization/models/matrix-data.model';
import {ContractFuelRate} from 'app/modules/organization/models/contract-fuel-rate.model';
import { forkJoin, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { UploadInitiateResponseDTO } from '../models/upload-initiate-response.model';
import { ContractsExportInitiateResponseDTO } from '../models/contracts-export-initiate-response.model';
import {
  BulkSaveRateSheetResponseDTO,
  UploadContractsByTypeRequestDTO,
  UploadContractsByTypeResponseDTO,
  UploadContractViewType,
  UploadPrepareBulkSaveResponseDTO,
  UploadRateSheetContract
} from '../models/upload-contracts-by-type.model';

@Injectable({
  providedIn: 'root'
})
export class ContractManagementService {

  constructor(private httpClient: HttpClient) {
  }

  saveRateCard(newRateCard: RateCard): Observable<RateCard> {
    const url = API.rateCard.saveRateCard;
    return this.httpClient.post<RateCard>(url, newRateCard);
  }

  getPartnerOrgs(verticals: string[], searchKey: string): Observable<Organization[]> {
    const url = API.organization.getPartnerOrgsByVerticals;
    let params = new HttpParams();
    if (verticals.length > 0) {
      params = params.set('verticals', verticals.toString());
    }
    params = params.set('searchKey', searchKey);
    return this.httpClient.get<Organization[]>(url, {params});
  }

  findOrgPartnersAndOnBoardedOrganizations(orgId: string, orgType: string, searchKey: string, verticals: string[]): Observable<Organization[]> {
    const url = API.organization.getOrganizationsByOrgIdAndOrgType.format([orgId, orgType]);
    let params = new HttpParams();
    params = params.append('searchKey', String(searchKey));
    params = params.append('verticals', verticals.toString());
    return this.httpClient.get<Organization[]>(url, {params});
  }

  findInitialRelatedOrganizationsPaginated(
    orgId: string,
    orgType: string,
    searchKey: string,
    verticals: string[],
    pageSize: number = 50
  ): Observable<Organization[]> {
    // Ensure we don't exceed 50 organizations for initial load
    const maxPageSize = Math.min(pageSize, 50);

    const url = API.organization.getPaginatedOrganizationsByOrgIdAndOrgType.format([orgId, orgType]);
    let params = new HttpParams();
    params = params.set('pageNum', '0'); // Always get first page only
    params = params.set('pageSize', String(maxPageSize));
    if (verticals && verticals.length > 0) {
      params = params.set('verticals', verticals.join(','));
    }
    if (searchKey && searchKey.trim() !== '') {
      params = params.set('searchKey', searchKey.trim());
    }

    return this.httpClient.get<Pageable<Organization>>(url, { params }).pipe(
      map(response => {
        const logType = searchKey && searchKey.trim() !== '' ? '🔍 Search' : '🚀 Initial load';
        console.log(`${logType}: Retrieved ${response.content.length}/${response.totalElements} organizations`);
        return response.content;
      })
    );
  }

  /**
   * Load organizations based on search criteria - used for search functionality
   */
  searchOrganizations(
    orgId: string,
    orgType: string,
    searchKey: string,
    verticals: string[],
    pageSize: number = 100
  ): Observable<Organization[]> {
    if (!searchKey || searchKey.trim() === '') {
      // If no search key, return initial organizations
      return this.findInitialRelatedOrganizationsPaginated(orgId, orgType, '', verticals, 50);
    }

    return this.findInitialRelatedOrganizationsPaginated(orgId, orgType, searchKey, verticals, pageSize);
  }

  findAllRelatedOrganizationsPaginated(
    orgId: string,
    orgType: string,
    searchKey: string,
    verticals: string[],
    pageSize: number = 400
  ): Observable<Organization[]> {
    const fetchPage = (pageNum: number): Observable<Pageable<Organization>> => {
      const url = API.organization.getPaginatedOrganizationsByOrgIdAndOrgType.format([orgId, orgType]);
      let params = new HttpParams();
      params = params.set('pageNum', String(pageNum));
      params = params.set('pageSize', String(pageSize));
      if (verticals && verticals.length > 0) {
        params = params.set('verticals', verticals.join(','));
      }
      if (searchKey) {
        params = params.set('searchKey', searchKey);
      }
      return this.httpClient.get<Pageable<Organization>>(url, { params });
    };

    const fetchAllPages = (pageNum: number, allOrganizations: Organization[] = []): Observable<Organization[]> =>
      fetchPage(pageNum).pipe(
        mergeMap(response => {
          const updatedOrganizations = [...allOrganizations, ...response.content];
          const hasMorePages =
            response.content.length === pageSize &&
            updatedOrganizations.length < response.totalElements;

          if (hasMorePages) {
            return fetchAllPages(pageNum + 1, updatedOrganizations);
          }

          console.log(`✅ Successfully retrieved all ${updatedOrganizations.length} organizations`);
          return of(updatedOrganizations);
        })
      );

    return fetchAllPages(0);
  }

  getAllTiers(orgId: string): Observable<RateTier[]> {
    const url = API.rateCard.getAllTiers.format([orgId]);
    return this.httpClient.get<RateTier[]>(url);
  }

  getRateCardsByFilters(orgId: string, customerId: string, vertical: string, pageNo: number, pageSize: number,
                        searchKey: string, contractType: ContractType, operationId?: string):
    Observable<Pageable<RateCard>> {
    let params = new HttpParams();
    if (customerId != null && customerId !== '') {
      params = params.set('customerId', customerId);
    }

    if (vertical != null && vertical !== '') {
      params = params.set('vertical', vertical);
    }
    params = params.set('searchKey', searchKey);
    params = params.set('type', contractType);
    if(operationId != null && operationId !== '' && operationId !== 'all'){
      params = params.set('operationId', operationId);
    }
    const url = API.rateCard.getRateCardsByFilters.format([orgId, pageNo.toString(), pageSize.toString()]);
    return this.httpClient.get<Pageable<RateCard>>(url, {params});
  }

  getRateCardsByOperationList(orgId: string, customerId: string, vertical: string, pageNo: number, pageSize: number,
                        searchKey: string, contractType: ContractType, operationIds: string[]):
    Observable<Pageable<RateCard>> {
    let params = new HttpParams();
    if (customerId != null && customerId !== '') {
      params = params.set('customerId', customerId);
    }

    if (vertical != null && vertical !== '') {
      params = params.set('vertical', vertical);
    }
    params = params.set('searchKey', searchKey);
    params = params.set('type', contractType);
    
    const url = API.rateCard.getRateCardsByOperationList.format([orgId, pageNo.toString(), pageSize.toString()]);
    return this.httpClient.post<Pageable<RateCard>>(url, operationIds, { params });
  }

  searchRateCards(orgId: string, filter: RateSheetFilterRequest, pageNo: number, pageSize: number):
    Observable<Pageable<RateCard>> {
    const url = API.rateCard.searchRateCards.format([orgId, pageNo.toString(), pageSize.toString()]);
    return this.httpClient.post<Pageable<RateCard>>(url, filter);
  }

  fetchCurrencies(): Observable<string[]> {
    const url = API.organization.getCurrencies;
    return this.httpClient.get<string[]>(url);
  }

  getVehicleByManagedByOrg(orgId: string, searchKey: string): Observable<Vehicle[]> {
    let params = new HttpParams();
    params = params.set('searchKey', searchKey);
    const url = API.organization.getVehiclesByManagingOrg.format([orgId]);
    return this.httpClient.get<Vehicle[]>(url, {params});
  }

  getVehicleByManagedByOrgAndCategory(orgId: string, searchKey: string, types: ResourceType[], vehicleCategory: string):
    Observable<Vehicle[]> {

    const url = API.organization.getVehiclesByManagingOrgFilter.format([orgId]);

    let params = new HttpParams();
    params = params.set('searchKey', searchKey);
    if (types != null) {
      params = params.append('vehicleTypes', types.toString());
    }
    params = params.append('vehicleCategory', vehicleCategory);

    return this.httpClient.get<Vehicle[]>(url, {params});
  }

  getVehicleCategories(): Observable<VehicleCategory[]> {
    const url = API.organization.getVehicleCategories;
    return this.httpClient.get<VehicleCategory[]>(url);
  }

  saveTier(newTier: RateTier): Observable<RateTier> {
    const url = API.rateCard.saveTier;
    return this.httpClient.post<RateTier>(url, newTier);
  }

  getOrgLocations_(orgId: string, searchValue: string): Observable<OrganizationLocation[]> {

    const url  = `${API.locations.getLocationsForWizard}`;
    let params = new HttpParams();
    if (searchValue !== ''){
      params = params.set('searchKey', searchValue);
    }
    params = params.set('orgTypes', ['ORG_PARTNER', 'PUBLIC'].toString());
    params = params.set('pageSize', 50 + '');
    params = params.set('pageNumber', 0 + '');
    return this.httpClient.get<OrganizationLocation[]>(url, {params});
  }

  getOrgLocationsDistanceMatrix(orgId: string, searchValue: string): Observable<OrganizationLocation[]> {

    const url  = `${API.locations.getLocationsForWizardExcludeExternal}`;
    let params = new HttpParams();
    if (searchValue !== ''){
      params = params.set('searchKey', searchValue);
    }
    params = params.set('orgTypes', ['ORG_PARTNER', 'PUBLIC'].toString());
    params = params.set('pageSize', 50 + '');
    params = params.set('pageNumber', 0 + '');
    return this.httpClient.get<OrganizationLocation[]>(url, {params});
  }

  getOrgLocationsByIds(locationIds: string[]): Observable<OrganizationLocation[]> {
    const url = `${API.locations.getLocationsForWizard}`;
    const chunks = this.chunkArray(locationIds, 50);

    const requests = chunks.map(chunk => {
      const filter = {
        _id: {
          $in: chunk
        }
      };
      let params = new HttpParams().set('filter', JSON.stringify(filter));
      params = params.set('orgTypes', ['ORG_PARTNER', 'PUBLIC'].toString());
      return this.httpClient.get<OrganizationLocation[]>(url, { params });
    });

    return forkJoin(requests).pipe(
      map(results => results.flat())
    );
  }

  getOrgLocations(
    orgId: string,
    searchValue: string,
    orgIds?: string[]
  ): Observable<OrganizationLocation[]> {
    return this.getPartnerOrgIdsByManagedOrgId(orgId).pipe(
      switchMap((ids: string[]) => {
        const mergedIds = orgIds ? [...ids, ...orgIds] : ids;
        const chunkSize = 500;
        const chunks = this.chunkArray(mergedIds, chunkSize);
        const url = `${API.locations.getLocationsForWizardAll}`;

        const requests = chunks.map(chunk => this.fetchAllLocationsForChunk(url, chunk, searchValue));

        return forkJoin(requests).pipe(
          map(results => results.flat())
        );
      })
    );
  }

  private fetchAllLocationsForChunk(url: string, chunk: string[], searchValue: string): Observable<OrganizationLocation[]> {
    const fetchPage = (pageNumber: number): Observable<OrganizationLocation[]> => {
      const filter = {
        where: {
          orgId: { inq: chunk }
        }
      };
      const requestBody = {
        orgTypes: ['ORG_PARTNER', 'PUBLIC'],
        filter,
        searchKey: searchValue || undefined,
        pageSize: 500,
        pageNumber
      };
      return this.httpClient.post<OrganizationLocation[]>(url, requestBody);
    };

    const fetchAllPages = (pageNumber: number, allLocations: OrganizationLocation[] = []): Observable<OrganizationLocation[]> =>
      fetchPage(pageNumber).pipe(
        mergeMap(locations => {
          const updatedLocations = [...allLocations, ...locations];
          if (locations.length === 500) {
            return fetchAllPages(pageNumber + 1, updatedLocations);
          }
          return of(updatedLocations);
        })
      );

    return fetchAllPages(0);
  }

  chunkArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }

  getPartnerOrgIdsByManagedOrgId(orgId: string, orgType?: string): Observable<string[]> {
    const url = API.organization.getPartnerOrgIdsByManagedOrgId.format([orgId]);
    let params = new HttpParams();
    if (orgType) {
      params = params.set('orgType', orgType);
    }
    return this.httpClient.get<string[]>(url, {params});
  }

  saveOperationDistanceMatrix(matrixData: MatrixDataTemplate): Observable<MatrixDataTemplate> {
    const url = API.rateCard.saveOperationDistanceMatrix;
    return this.httpClient.post<MatrixDataTemplate>(url, matrixData);
  }

  getOperationDistanceMatrix(operationId: string, orgId: string): Observable<MatrixDataTemplate> {
    const url = API.rateCard.getOperationDistanceMatrix.format([operationId, orgId]);
    return this.httpClient.get<MatrixDataTemplate>(url);
  }

  getDistanceMatrixTemplateOperationListWise(
    orgId?: string,
    searchKey?: string,
    operationIdList?: string[]
  ): Observable<MatrixDataTemplate[]> {
    const url = API.rateCard.getDistanceMatrixOperationList.format([orgId]);
    const body = {
      searchKey,
      operationIdList
    };
    return this.httpClient.post<MatrixDataTemplate[]>(url, body);
  }

  getFuelRates(operationId: string, orgId: string): Observable<ContractFuelRate[]> {
    const url = API.organization.getFuelRates.format([orgId, operationId]);
    return this.httpClient.get<ContractFuelRate[]>(url);
  }

  saveFuelRates(operationId: string, orgId: string, fuelRates: ContractFuelRate): Observable<ContractFuelRate[]> {
    const url = API.organization.updateFuelRates.format([orgId, operationId]);
    return this.httpClient.post<ContractFuelRate[]>(url, fuelRates);
  }

  deleteFuelRate(operationId: string, orgId: string, fuelRateId: string): Observable<void> {
    const url = API.organization.deleteFuelRates.format([orgId, operationId, fuelRateId]);
    return this.httpClient.delete<void>(url);
  }

  updateFuelRateContracts(operationId: string, orgId: string, fuelRateContracts: ContractFuelRate): Observable<ContractFuelRate[]> {
    const url = API.organization.updateFuelRateContracts.format([orgId, operationId]);
    return this.httpClient.post<ContractFuelRate[]>(url, fuelRateContracts);
  }

  removeFuelRateEquationForOperations(orgId: string, workflowId: string, rateCardId: string,
    removeOperationIds: string[]): Observable<void> {
    const url = API.organization.removeFuelRateEquationForOperations.format([orgId, workflowId, rateCardId]);
    return this.httpClient.post<void>(url, removeOperationIds);
  }

  uploadRateSheetFile(file: File, contractType?: ContractType): Observable<UploadInitiateResponseDTO> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    let params = new HttpParams();
    if (contractType) {
      params = params.set('contractType', contractType);
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      params = params.set('timezone', timezone);
    }
    const url = API.rateCard.uploadRateSheet;
    return this.httpClient.post<UploadInitiateResponseDTO>(url, formData, { params });
  }

  getUploadContractsByType(
    orgId: string,
    eventId: string,
    type: UploadContractViewType,
    page: number = 0,
    requestBody?: UploadContractsByTypeRequestDTO | null
  ): Observable<UploadContractsByTypeResponseDTO> {
    const url = API.rateCard.getUploadContractsByType;
    let params = new HttpParams()
      .set('orgId', orgId)
      .set('eventId', eventId)
      .set('type', type)
      .set('page', String(page));
    return this.httpClient.post<UploadContractsByTypeResponseDTO>(url, requestBody ?? null, { params });
  }

  prepareBulkSave(
    orgId: string,
    eventId: string
  ): Observable<UploadPrepareBulkSaveResponseDTO> {
    const url = API.rateCard.prepareBulkSaveRateSheets;
    const params = new HttpParams().set('orgId', orgId).set('eventId', eventId);
    return this.httpClient.post<UploadPrepareBulkSaveResponseDTO>(url, null, { params });
  }

  bulkSaveRateSheets(
    orgId: string,
    eventId: string
  ): Observable<BulkSaveRateSheetResponseDTO> {
    const url = API.rateCard.bulkSaveRateSheets;
    const params = new HttpParams().set('orgId', orgId).set('eventId', eventId);
    return this.httpClient.post<BulkSaveRateSheetResponseDTO>(url, null, { params });
  }

  downloadErrorExcel(orgId: string, eventId: string): Observable<Blob> {
    const url = API.rateCard.downloadErrorExcel;
    const params = new HttpParams().set('orgId', orgId).set('eventId', eventId);
    return this.httpClient.get(url, { params, responseType: 'blob' });
  }

  downloadContractsExcel(
    orgId: string,
    contractType: ContractType,
    filter?: RateSheetFilterRequest,
  ): Observable<ContractsExportInitiateResponseDTO> {
    const url = API.rateCard.exportContractsExcel.format([orgId]);
    let params = new HttpParams().set('contractType', contractType);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      params = params.set('timezone', timezone);
    }
    return this.httpClient.post<ContractsExportInitiateResponseDTO>(url, filter ?? null, { params });
  }

  downloadSingleContractExcel(rateCardId: string, type: string): Observable<Blob> {
    const url = API.rateCard.exportSingleContractExcel.format([rateCardId]);
    let params = new HttpParams().set('type', type);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      params = params.set('timezone', timezone);
    }
    return this.httpClient.get(url, { params, responseType: 'blob' });
  }

  updateUploadContract(
    orgId: string,
    eventId: string,
    contract: UploadRateSheetContract
  ): Observable<UploadRateSheetContract> {
    const url = API.rateCard.updateUploadContract;
    const params = new HttpParams().set('orgId', orgId).set('eventId', eventId);
    return this.httpClient.put<UploadRateSheetContract>(url, contract, { params });
  }
}
